import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const COPY = {
  de: { cta: "Jetzt anfragen", wa: "WhatsApp" },
  en: { cta: "Request now", wa: "WhatsApp" },
  es: { cta: "Solicitar ahora", wa: "WhatsApp" },
};

/**
 * Sticky CTA bar shown ONLY on mobile (md:hidden) on service-detail pages.
 * Appears after the user has scrolled past the hero (~600px) so it doesn't
 * compete with the in-hero CTAs. Disappears when the actual contact form
 * comes into view to avoid duplicate CTAs on screen.
 */
const ServiceMobileCta = ({ waUrl, anchorId = "service-anfrage" }) => {
  const { locale } = useLanguage();
  const t = COPY[locale] || COPY.de;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const SHOW_AFTER_PX = 560;
    const onScroll = () => {
      if (typeof window === "undefined") return;
      const scrolled = window.scrollY > SHOW_AFTER_PX;
      const form = document.getElementById(anchorId);
      let formInView = false;
      if (form) {
        const rect = form.getBoundingClientRect();
        formInView = rect.top < window.innerHeight * 0.85 && rect.bottom > 0;
      }
      setVisible(scrolled && !formInView);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [anchorId]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          data-testid="service-mobile-cta"
          className="fixed inset-x-0 bottom-0 z-40 md:hidden px-3 pb-3"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0) + 12px)" }}
        >
          <div className="rounded-2xl bg-neutral-950/95 backdrop-blur-md border border-white/10 shadow-[0_18px_40px_rgba(0,0,0,0.35)] p-2 flex items-center gap-2">
            <a
              href={`#${anchorId}`}
              data-testid="service-mobile-cta-primary"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-white text-neutral-950 px-4 py-3 text-sm font-semibold hover:bg-neutral-100 transition-colors"
            >
              {t.cta}
              <ArrowRight className="w-4 h-4" />
            </a>
            {waUrl && (
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={t.wa}
                data-testid="service-mobile-cta-wa"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 text-white px-4 py-3 text-sm font-semibold hover:bg-emerald-600 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
              </a>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ServiceMobileCta;
